import { FieldFeedback } from 'motion-components';
import { Form as FForm, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Button, Form, InputGroup, Modal } from 'react-bootstrap';
import { FaEye } from 'react-icons/fa';
import { createModal } from 'react-modal-promise';
import { useRootStore } from 'store';
import { InferType, object, ref, string } from 'yup'; // for only what you need
//  /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{6,})/,

import gql from 'graphql-tag';
import * as ApolloReactHooks from '@apollo/client';

const UserLoginDocument = gql`
  query userLogin($userName: String, $userPassword: String) {
    userLogin(userName: $userName, userPassword: $userPassword) {
      jwt
      userMustChangePassword
      expirationDate
      userId
      userFirstName
      userLastName
    }
  }
`;

function useUserLoginLazyQuery(baseOptions?: ApolloReactHooks.LazyQueryHookOptions<any, any>) {
  return ApolloReactHooks.useLazyQuery<any, any>(UserLoginDocument, baseOptions);
}

const UserSetNewPasswordDocument = gql`
  mutation userSetNewPassword($userName: String!, $userNewPassword: String!) {
    userSetNewPassword(userName: $userName, userNewPassword: $userNewPassword)
  }
`;
function useUserSetNewPasswordMutation(
  baseOptions?: ApolloReactHooks.MutationHookOptions<any, any>,
) {
  return ApolloReactHooks.useMutation<any, any>(UserSetNewPasswordDocument, baseOptions);
}

/**
 * Setup Yup Validation
 */
const validationSchema = object().shape({
  password: string()
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{6,})/,
      'Πρέπει να είναι τουλάχιστον 6 χαρακτήρες και να περιέχει τουλάχιστον ένα Κεφαλαίο γράμμα, ένα Μικρό, και έναν Αριθμό',
    )
    .required('Το πεδίο δεν μπορεί να είναι κενό'),
  confirmPassword: string()
    .oneOf([ref('password'), null], 'Η επιβεβαίωση δεν είναι σωστή')
    .required('Το πεδίο δεν μπορεί να είναι κενό'),
});

export type FormDataType = InferType<typeof validationSchema>;

/** Στυλ κουμπιού εμφάνισης κωδικού — ίδιο ύψος με το πεδίο μέσω input-group flex */
const passwordToggleBtnStyle: React.CSSProperties = {
  alignSelf: 'stretch',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 12px',
  width: '42px',
};

type Props = {
  open: boolean;
  close: () => void;
};

const SetPasswordModal = ({ open, close }: Props) => {
  /*-------------------- Body -----------------------------------------------*/
  const { appStore } = useRootStore();

  const [setPassword] = useUserSetNewPasswordMutation();
  const [get, { data }] = useUserLoginLazyQuery();

  const [passwordShow, setPasswordShow] = useState(false);
  const [confirmPasswordShow, setConfirmPasswordShow] = useState(false);

  // * 1. Effect οταν πάρω data
  useEffect(() => {
    if (!!data) {
      const userLogin = data?.userLogin;
      appStore.login(appStore.user, userLogin);
      close();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  /*-------------------- Handlers -------------------------------------------*/
  const handleSubmitForm = (values: FormDataType, action) => {
    console.log(values);
    setPassword({
      variables: { userName: appStore.user, userNewPassword: (values as any).password },
    }).then(() => {
      get({ variables: { userName: appStore.user, userPassword: (values as any).password } });
    });
  };

  /*-------------------- Render ---------------------------------------------*/
  return (
    <Modal show={open} centered backdrop="static">
      <Modal.Header>
        <Modal.Title>Αλλαγή Κωδικού Πρόσβασης</Modal.Title>
      </Modal.Header>
      <Formik
        onSubmit={handleSubmitForm}
        initialValues={{ password: '', confirmPassword: '' }}
        validationSchema={validationSchema}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {(formikBag) => (
          <Form as={FForm}>
            <Modal.Body>
              <Form.Group>
                <Form.Label className="mb-0">Νέος Κωδικός</Form.Label>
                <InputGroup>
                  <Form.Control
                    id="password"
                    type={passwordShow ? 'text' : 'password'}
                    {...formikBag.getFieldProps('password')}
                    isInvalid={
                      !!(formikBag as any).touched.password && !!(formikBag as any).errors.password
                    }
                  />
                  <Button
                    onClick={() => setPasswordShow((value) => !value)}
                    style={passwordToggleBtnStyle}
                    variant="secondary"
                  >
                    <FaEye />
                  </Button>
                  <FieldFeedback type="invalid" name={'password'} />
                </InputGroup>
              </Form.Group>

              <Form.Group className="mt-3 mb-4">
                <Form.Label className="mb-0">Επιβεβαίωση Κωδικού</Form.Label>
                <InputGroup>
                  <Form.Control
                    id="confirmPassword"
                    type={confirmPasswordShow ? 'text' : 'password'}
                    {...formikBag.getFieldProps('confirmPassword')}
                    isInvalid={
                      !!(formikBag as any).touched.confirmPassword &&
                      !!(formikBag as any).errors.confirmPassword
                    }
                  />
                  <Button
                    onClick={() => setConfirmPasswordShow((value) => !value)}
                    style={passwordToggleBtnStyle}
                    variant="secondary"
                  >
                    <FaEye />
                  </Button>
                  <FieldFeedback type="invalid" name={'confirmPassword'} />
                </InputGroup>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" type="submit" style={{ width: '120px' }}>
                {'ΟΚ'}
              </Button>
              {!appStore.userLogin.userMustChangePassword && (
                <Button variant="danger" onClick={() => close()} style={{ width: '120px' }}>
                  {'ΑΚΥΡΟ'}
                </Button>
              )}
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

const setPasswordModal = createModal(SetPasswordModal);

export default setPasswordModal;
